// file: client/src/lib/csv-export.js
/**
 * PHARMAKON — CSV download helper
 *
 * One-stop helper that takes any `*Api.exportCsv(filters)` (or a raw URL +
 * filters), fetches the CSV as a Blob through the authenticated axios
 * client, then triggers a browser download via `<a download>`.
 *
 * Why this helper instead of inline code:
 *   • The axios response interceptor returns `res.data` directly, so a Blob
 *     response is already unwrapped — calls look identical to plain GETs.
 *   • Filenames need to be derived from the response's Content-Disposition
 *     header when present (the server picks a timestamped name), with a
 *     fallback to `<resource>-<ISO>.csv` for resilience.
 *   • Object URLs must be revoked after the click or memory leaks across
 *     repeated exports on the same page.
 */
import api from "@/services/api.client.js";

/**
 * Parse RFC 6266-ish `filename="…"` out of a Content-Disposition header.
 * Returns null when none is present (interceptor strips response headers,
 * so this is best-effort — we still have a safe fallback).
 */
function _filenameFromHeaders(headers, fallback) {
  const cd = headers?.["content-disposition"] ?? headers?.["Content-Disposition"];
  if (cd && typeof cd === "string") {
    const m = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const raw = m?.[1] ?? m?.[2];
    if (raw) {
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
  }
  return fallback;
}

/**
 * Trigger a browser download for a Blob.
 * @private
 */
function _download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  // Required on Firefox: anchor must be in the document for the click to
  // fire a download. Append + remove keeps the DOM clean.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the browser one frame to handle the click before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Default ISO-stamped filename. Mirrors the server's format so manual
 * fallbacks look identical to server-provided ones.
 */
export function defaultCsvFilename(resource) {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  return `${resource}-${stamp}.csv`;
}

/**
 * Fetch a CSV from `path` with the given query `params` and trigger a
 * browser download. Returns a Promise that resolves once the download has
 * been triggered (the Promise rejecting means the user saw nothing — caller
 * should surface an error toast).
 *
 * IMPORTANT: this bypasses the response interceptor's `r.data` unwrap by
 * passing `responseType: 'blob'`. Axios still resolves with the response,
 * which the interceptor returns as `.data` — so the resolved value IS the
 * Blob. Headers are not part of the resolved value (the interceptor drops
 * them), so the helper falls back to a client-generated filename when no
 * server-side name leaks through.
 *
 * @param {string} path        API path, e.g. "/produits/csv"
 * @param {object} [params]    Query params — same shape as the JSON list call
 * @param {string} [resource]  Filename stem when no Content-Disposition is available
 */
export async function downloadCsv(path, params = {}, resource = "export") {
  // Strip empty strings, undefined, null — keeps the URL tidy and prevents
  // server-side validators from choking on `?status=` etc.
  const clean = {};
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v === "" || v === null || v === undefined) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    clean[k] = v;
  }

  const blob = await api.get(path, {
    params: clean,
    responseType: "blob",
    // Increase timeout: large exports can take a few seconds. Default is 15s.
    timeout: 60_000,
  });
  // The interceptor returns res.data only — so `blob` IS the Blob object.
  // Defensive: when something upstream changes, fall back to wrapping.
  const finalBlob =
    blob instanceof Blob
      ? blob
      : new Blob([typeof blob === "string" ? blob : JSON.stringify(blob)], {
          type: "text/csv;charset=utf-8",
        });

  const filename = _filenameFromHeaders(undefined, defaultCsvFilename(resource));
  _download(finalBlob, filename);
}
